import React, { useState, useEffect } from 'react';
import UserSearchInput from './UserSearchInput';
import { IconTrash } from './Icons';

// As 6 regionais do portal (espelha REGION_FOLDERS de api/_shared/roleConfig.js).
// Usadas no seletor de vínculo do grupo regional (req_ajuste_02, itens 2 e 3).
const REGIONS = [
  'Regional Ms',
  'Regional Mt',
  'Regional Pr 01',
  'Regional Pr 02',
  'Regional Pr 3',
  'Regional Rs'
];

// Badge simples (inline) para fonte/escopo/status de um membro de grupo.
function Badge({ children, tone }) {
  const tones = {
    cargo: { bg: '#e0edff', fg: '#1b4f9c' },
    manual: { bg: '#fff3d6', fg: '#8a5a00' },
    scope: { bg: '#e7f5ec', fg: '#1f7a44' },
    excluded: { bg: '#fde2e1', fg: '#a12622' }
  };
  const c = tones[tone] || { bg: '#eee', fg: '#444' };
  return (
    <span style={{
      background: c.bg, color: c.fg, borderRadius: 10, padding: '1px 8px',
      fontSize: 11, fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap'
    }}>{children}</span>
  );
}

// Gestão de um grupo derivado de cargo (regional | admcen): mostra quem entra por
// CARGO e quem foi incluído MANUALMENTE, e permite incluir/excluir (CR-3).
// Reutilizado pela aba Admin (gestão de acessos) e pela tela Enviar Documentos
// (compartilhadores também gerenciam grupos — CR FASE 3, item 2).
function GroupManager({ title, icon, description, group }) {
  const [members, setMembers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newScope, setNewScope] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const isRegional = group === 'regional';

  const load = async () => {
    setListLoading(true);
    try {
      const res = await fetch(`/api/listGroupMembers?group=${group}`);
      const data = await res.json();
      if (!data.error) setMembers(data.members || []);
      else setMessage({ error: data.error });
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addEmail = async (email, scope) => {
    if (!email || !email.includes('@')) {
      setMessage({ error: 'Email inválido' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const body = { group, email };
      if (isRegional && scope) body.scope = scope;
      const res = await fetch('/api/addGroupMember', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ success: `${email} incluído` });
        setNewEmail('');
        setNewScope('ALL');
        load();
      } else {
        setMessage({ error: data.error });
      }
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Define/ajusta a regional de um membro (override manual — req_ajuste_02, item 3).
  const setMemberScope = async (email, scope) => {
    setScopeBusy(true);
    setMessage(null);
    try {
      const res = await fetch('/api/addGroupMember', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group: 'regional', email, scope })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ success: `Regional de ${email} atualizada` });
        load();
      } else {
        setMessage({ error: data.error });
      }
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setScopeBusy(false);
    }
  };

  // Remove o vínculo manual de regional: o membro volta ao padrão do cargo.
  const revertMemberScope = async (email) => {
    setScopeBusy(true);
    setMessage(null);
    try {
      const res = await fetch(
        `/api/clearGroupScope?group=regional&email=${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (data.success) {
        setMessage({ success: `Vínculo de ${email} removido (voltou ao padrão do cargo)` });
        load();
      } else {
        setMessage({ error: data.error });
      }
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setScopeBusy(false);
    }
  };

  // Troca no seletor de regional de um membro ativo.
  const onScopeChange = (m, value) => {
    if (value === (m.scope || 'ALL')) return;
    if (value === 'ALL') {
      // Membro por cargo: reverter ao padrão do cargo (remove o override).
      // Membro manual: gravar 'Todas' explicitamente (senão sairia do grupo).
      if (m.source === 'cargo') revertMemberScope(m.email);
      else setMemberScope(m.email, 'ALL');
    } else {
      setMemberScope(m.email, value);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remover ${email} do grupo?`)) return;
    try {
      const res = await fetch(
        `/api/removeGroupMember?group=${group}&email=${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (data.success) {
        setMessage({ success: `${email} removido` });
        load();
      } else {
        setMessage({ error: data.error });
      }
    } catch (err) {
      setMessage({ error: err.message });
    }
  };

  const ativos = members.filter((m) => m.active).length;

  return (
    <div className="upload-form" style={{ marginTop: 24 }}>
      <h3 className="section-title-admin"><span className="section-icon">{icon}</span>{title}</h3>
      {description && <p className="section-description">{description}</p>}

      <div className="form-group">
        <label>Incluir manualmente</label>
        <div className="add-admin-row">
          <UserSearchInput
            value={newEmail}
            onChange={setNewEmail}
            placeholder="Buscar por nome ou e-mail…"
            disabled={loading}
          />
          {/* No grupo regional o botão vai p/ a linha da regional (abaixo), então
              o campo de nome ocupa a linha inteira. */}
          {!isRegional && (
            <button className="add-admin-button" onClick={() => addEmail(newEmail, newScope)} disabled={loading}>
              {loading ? '...' : '+ Incluir'}
            </button>
          )}
        </div>
        {isRegional && (
          <div className="add-scope-row">
            <span className="scope-label">Regional do membro:</span>
            <select
              className="scope-select"
              value={newScope}
              onChange={(e) => setNewScope(e.target.value)}
              disabled={loading}
              aria-label="Regional do membro"
            >
              <option value="ALL">Todas as regionais</option>
              {REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <button
              className="add-admin-button add-scope-button"
              onClick={() => addEmail(newEmail, newScope)}
              disabled={loading}
            >
              {loading ? '...' : '+ Incluir'}
            </button>
          </div>
        )}
      </div>

      {message && (
        <div className={`upload-result ${message.success ? 'success' : 'error'}`}>
          <p>{message.success || message.error}</p>
        </div>
      )}

      <div className="admins-list">
        <label>Membros ({ativos} ativos de {members.length})</label>
        {listLoading ? (
          <p className="empty-admins">Carregando membros do AD...</p>
        ) : members.length === 0 ? (
          <p className="empty-admins">Nenhum membro.</p>
        ) : (
          <ul>
            {members.map((m, i) => (
              <li key={i} className="admin-item" style={{ opacity: m.active ? 1 : 0.55 }}>
                <div>
                  <span className="admin-email">
                    {m.email}
                    {m.source === 'cargo' ? <Badge tone="cargo">Cargo</Badge> : <Badge tone="manual">Manual</Badge>}
                    {isRegional && m.scope && !m.active && (
                      <Badge tone="scope">{m.scope === 'ALL' ? 'Todas' : m.scope}</Badge>
                    )}
                    {isRegional && m.active && m.hasScopeOverride && (
                      <Badge tone="manual">Vínculo manual</Badge>
                    )}
                    {!m.active && <Badge tone="excluded">Excluído</Badge>}
                  </span>
                  <span className="admin-meta">
                    {m.source === 'cargo'
                      ? [m.jobTitle, m.officeLocation].filter(Boolean).join(' • ') || '—'
                      : `Inclusão manual${m.addedBy ? ` por ${m.addedBy}` : ''}`}
                  </span>
                  {isRegional && m.active && (
                    <div className="scope-control">
                      <span className="scope-label">Regional:</span>
                      <select
                        className="scope-select"
                        value={m.scope || 'ALL'}
                        onChange={(e) => onScopeChange(m, e.target.value)}
                        disabled={scopeBusy}
                        aria-label={`Regional de ${m.email}`}
                      >
                        <option value="ALL">Todas as regionais</option>
                        {REGIONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      {m.hasScopeOverride && m.source === 'cargo' && (
                        <button
                          type="button"
                          className="scope-revert"
                          onClick={() => revertMemberScope(m.email)}
                          disabled={scopeBusy}
                        >
                          voltar ao padrão
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {m.active ? (
                  <button className="remove-admin-button" onClick={() => handleRemove(m.email)} aria-label={`Remover ${m.email}`}>
                    <IconTrash size={17} />
                  </button>
                ) : (
                  <button className="add-admin-button" onClick={() => addEmail(m.email, m.scope)}>
                    Reincluir
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default GroupManager;
